import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { Box, Drawer, Snackbar } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  Alert,
  Chatbot,
  Footer,
  HeaderV2,
  Header,
  Layout,
  Loader,
  NotificationDrawer,
  ReportDialog,
  SessionTimeoutDialog,
  SurveyConfirmation,
  SurveyDialog,
  NewLearningSurveyDialog,
} from "components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation, useBlocker } from "react-router-dom";
import { RootState, useAppDispatch } from "store";
import * as UserContextTypes from "utils/graphql/UserContext";
import * as PortalDisplayTypes from "utils/graphql/PortalDisplayInfo";
import * as LocalizationTypes from "utils/graphql/Localization";
import {
  RFRESH_GET_USERCONTEXT,
  GET_NOTIFICATIONS,
  GET_NOTIFICATIONS_METRICS,
  GET_USER_CONTEXT,
  SKIP_SURVEY,
  PORTAL_DISPLAY_INFO,
  LOCALIZATION,
  CLEAR_SESSION,
  WORKFLOW_STATE,
} from "utils/queries";
import "@progress/kendo-theme-material/dist/all.css";
import "./App.css";
import routes from "./routes";
import {
  BookingJourney_Paths,
  SESSION_STORAGE_KEYS,
  HEADER_CLICK_TYPES,
  IBF_ENABLED,
  KLP_SERVICE_NOW_WINDOW_NAME,
} from "utils/constants";
import { tenantSettings } from "utils/authentication/tenantSettings";
import SessionManager from "utils/sessionManager";
import { Notifications } from "utils/graphql/Notifications";
import i18n, { local_keys, TRANSLATION_STORAGE_KEY } from "utils/i18n";
import { useTranslation } from "react-i18next";
import { WorkflowState } from "utils/graphql/WorkflowState";
import { pick } from "lodash";
import portalSettingsManager from "utils/portalSettingsManager";
import { logger } from "utils/logger";
import { NotificationMetrics } from "utils/graphql/NotificationMetrics";
import {
  addOrUpdateSessionData,
  openContactUs,
  LIFECYCLE_STORAGE_KEY,
  isStageTerminal,
  isValidStage,
} from "utils/helpers";
import { openUrlUtil } from "hooks/open-url";
import { useCancelLearningRequest } from "hooks/useCancelLearningRequest";
import {
  clearOpenSurveyRequest,
  requestOpenSurvey,
  setWorkflowStage,
} from "store/newLearningRequest";
import { WorkflowStageEnum } from "utils/types";

interface AppProps {
  onSilentRenew: () => void;
  onLogout: () => void;
  isAuthenticated: boolean;
}

const useStyles = makeStyles({
  snackBarAnchorOriginTopRight: {
    top: "64px !important",
  },
});

function App({ onSilentRenew, isAuthenticated, onLogout }: AppProps): React.ReactElement {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const [openSurvey, setOpenSurvey] = useState<boolean>(false);
  const [openMultiStepSurvey, setOpenMultiStepSurvey] = useState<boolean>(false);
  const [isSurveyCompleted, setIsSurveyCompleted] = useState<boolean>(false);
  const [surveyConfirmation, setSuveyConfirmation] = useState<boolean>(false);
  const [showReportDialog, setShowReportDialog] = useState<boolean>(false);
  const navigationBlocked = useSelector((state: RootState) => state.core.navigationBlocked);
  const courseInProgress = useSelector((state: RootState) => state.core.courseInProgress);
  const [isClearNotification, setIsClearNotification] = useState<boolean>(false);
  const [isTranslationsLoaded, setIsTranslationsLoaded] = useState<boolean>(false);
  const [isPortalSettignsLoaded, setIsPortalSettignsLoaded] = useState<boolean>(false);
  const [totalNotificationsCount, setTotalNotificationsCount] = useState<number>(0);
  const [totalUnreadNotificationsCount, setTotalUnreadNotificationsCount] = useState<number>(0);
  const workflowStage = useSelector((state: RootState) => state.newLearningRequest.workflowStage);
  const isOpenSurveyRequested = useSelector(
    (state: RootState) => state.newLearningRequest.isOpenSurveyRequested,
  );
  const navigate = useNavigate();
  const handleNavigate = (path: string) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const { cancel } = useCancelLearningRequest();

  const [loadWorkflowStateGraph, { data: workflowStateData, error: workflowStateError }] =
    useLazyQuery<WorkflowState>(WORKFLOW_STATE, {
      fetchPolicy: "no-cache",
    });

  // Apollo Client v4: Handle data/error with useEffect instead of onCompleted/onError
  useEffect(() => {
    if (workflowStateData) {
      const stage = (workflowStateData?.workflowState?.workflowState?.workflowStage ??
        null) as WorkflowStageEnum | null;
      dispatch(setWorkflowStage(stage));
    }
  }, [workflowStateData, dispatch]);

  useEffect(() => {
    if (workflowStateError) {
      logger.error("Failed to load workflow state", {
        error:
          workflowStateError instanceof Error
            ? workflowStateError.message
            : String(workflowStateError),
      });
      dispatch(setWorkflowStage(WorkflowStageEnum.Failed));
    }
  }, [workflowStateError, dispatch]);

  const classes = useStyles();

  const navigationBlockMessage = useSelector(
    (state: RootState) => state.core.navigationBlockMessage,
  );
  const isLoading = useSelector((state: RootState) => state.core.loader);

  // Block in-app navigation when navigationBlocked is true
  const blocker = useBlocker(navigationBlocked);

  // Handle blocked navigation with confirmation dialog
  useEffect(() => {
    if (blocker.state === "blocked") {
      const message = navigationBlockMessage || t("Are_you_sure_want_to_leave");
      if (window.confirm(message)) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker, navigationBlockMessage, t]);

  const isContentPage = location.pathname.toLowerCase().indexOf("/player") === -1;

  const [handleLogin, { data: loginData, called, loading, error }] =
    useLazyQuery<any>(CLEAR_SESSION);
  const [loadUserContext, { data }] = useLazyQuery<UserContextTypes.UserContext>(GET_USER_CONTEXT);

  const { data: portalDisplayData, error: portalDisplayError } =
    useQuery<PortalDisplayTypes.PortalDisplayInfo>(PORTAL_DISPLAY_INFO, {
      skip: !isAuthenticated || !isContentPage,
    });

  // Apollo Client v4: Handle data/error with useEffect instead of onCompleted/onError
  useEffect(() => {
    if (portalDisplayData?.portalDisplayInformation) {
      portalSettingsManager.Initialize(portalDisplayData.portalDisplayInformation || []);
      addOrUpdateSessionData(
        { [IBF_ENABLED]: portalSettingsManager?.application?.features?.enableIBFFlow },
        SESSION_STORAGE_KEYS.FEATURES,
      );
      setIsPortalSettignsLoaded(true);
    }
  }, [portalDisplayData]);

  useEffect(() => {
    if (portalDisplayError) {
      logger.error(portalDisplayError?.message);
      setIsPortalSettignsLoaded(true);
    }
  }, [portalDisplayError]);

  const onOpenNewLearningStepSurvey = useCallback(async () => {
    try {
      const hasTerminalLifecycle = isStageTerminal(workflowStage);

      if (!hasTerminalLifecycle) {
        dispatch({
          type: "loader/showandhide",
          payload: { show: true, message: t("learning_request") },
        });

        const cancelled = await cancel();
        if (cancelled) {
          dispatch(setWorkflowStage(WorkflowStageEnum.Cancelled));
        } else {
          dispatch(setWorkflowStage(WorkflowStageEnum.Failed));
        }
      }
    } catch (e) {
      logger.error("cancelLearningRequestFlow failed on open", {
        error: e instanceof Error ? e.message : String(e),
      });
    }

    dispatch({
      type: "loader/showandhide",
      payload: { show: false, message: t("learning_request") },
    });
    setOpenMultiStepSurvey(true);
  }, [cancel, dispatch, t, workflowStage]);

  const requestOpenNewLearningStepSurvey = useCallback(() => {
    dispatch(requestOpenSurvey());
  }, [dispatch]);

  const { data: localizationData, error: localizationError } =
    useQuery<LocalizationTypes.Localization>(LOCALIZATION, {
      skip: !isAuthenticated,
    });

  // Apollo Client v4: Handle localization data with useEffect instead of onCompleted/onError
  useEffect(() => {
    if (localizationData?.localizationData) {
      const translations: { [key: string]: string } = {};
      localizationData.localizationData.forEach(
        (localization: LocalizationTypes.Localization_localizationData | null) => {
          if (localization?.code && localization?.localText) {
            translations[localization.code] = localization.localText;
          } else {
            logger.error(
              `Localization data is not in the correct format: ${JSON.stringify(localization)}`,
            );
          }
        },
      );
      i18n.addResourceBundle("en", "translation", translations, true, true);
      setIsTranslationsLoaded(true);
      sessionStorage.setItem(
        TRANSLATION_STORAGE_KEY,
        JSON.stringify(pick(translations, local_keys)),
      );
    }
  }, [localizationData]);

  useEffect(() => {
    if (localizationError) {
      logger.error(`get translations failed ${localizationError}`);
      setIsTranslationsLoaded(true);
    }
  }, [localizationError]);

  const [skipSurvey] = useMutation(SKIP_SURVEY);
  const toasterAlert = useSelector((state: RootState) => state.core.toasterAlert);
  const [openNotification, setOpenNotification] = useState<boolean>(false);

  const handleSkipSurvey = () => {
    skipSurvey({ variables: { surveyId: data?.userContext?.surveyId } });
  };
  // Apollo Client v4: useLazyQuery execute function returns Promise
  const [clearTokenStore] = useLazyQuery<any>(CLEAR_SESSION);

  const handleClearTokenStore = async () => {
    await clearTokenStore();
    postLogout();
  };
  const [loadNotifications, { data: notificationResult }] =
    useLazyQuery<Notifications>(GET_NOTIFICATIONS);
  const [loadNotificationMetrics, { data: notificationMetrics }] =
    useLazyQuery<NotificationMetrics>(GET_NOTIFICATIONS_METRICS);

  // Apollo Client v4: Handle result with returned data/error
  const [refreshUserContext, { data: refreshData, error: refreshError }] =
    useLazyQuery<any>(RFRESH_GET_USERCONTEXT);

  useEffect(() => {
    if (refreshData?.refreshPermissionsforUser) {
      dispatch({
        type: "userContext/load",
        payload: { userContext: { ...refreshData.refreshPermissionsforUser } },
      });
    }
  }, [refreshData, dispatch]);

  useEffect(() => {
    if (refreshError) {
      logger.error(refreshError?.message);
    }
  }, [refreshError]);

  const handleRefetchNotifications = () => {
    loadNotifications();
    loadNotificationMetrics();
  };

  // Dispatch user context to Redux when loaded
  // Skip dispatching if refreshUserContext has already set permissionsLoaded=true
  // to avoid race condition where stale cache data overwrites refreshed data
  useEffect(() => {
    if (data?.userContext && !refreshData?.refreshPermissionsforUser?.permissionsLoaded) {
      dispatch({
        type: "userContext/load",
        payload: { userContext: { ...data.userContext } },
      });
    }
  }, [data, refreshData, dispatch]);

  useEffect(() => {
    if (isAuthenticated && isContentPage) {
      if (data) {
        loadNotifications();
        loadNotificationMetrics();
      }
      logger.setAuthenticatedUserContext(
        data?.userContext?.learnerEmail,
        data?.userContext?.learnerId,
        true,
      );
      if (data?.userContext?.isSurveySkipped === false && data?.userContext?.showSurvey === true) {
        setSuveyConfirmation(true);
      }
    }
  }, [dispatch, data, isAuthenticated]);

  useEffect(() => {
    if (!isOpenSurveyRequested) return;

    dispatch(clearOpenSurveyRequest());

    void onOpenNewLearningStepSurvey();
  }, [dispatch, isOpenSurveyRequested, onOpenNewLearningStepSurvey]);

  // Handle loginData when it becomes available after handleLogin
  useEffect(() => {
    if (loginData) {
      sessionStorage.setItem("isLogin", "false");
      loadUserContext();
      refreshUserContext();
    }
  }, [loginData]);

  // sync lifecycle across tabs — only when New Learning Request feature is enabled
  useEffect(() => {
    if (!portalSettingsManager?.application?.features?.enableNewLearningRequest) return;
    // If portal settings are loaded and user is on content pages, start workflow polling
    if (isPortalSettignsLoaded && isAuthenticated && isContentPage) {
      loadWorkflowStateGraph();
    }

    const handler = (e: StorageEvent) => {
      if (e.key === LIFECYCLE_STORAGE_KEY && isValidStage(e.newValue)) {
        dispatch(setWorkflowStage(e.newValue));
      }
    };
    globalThis.addEventListener("storage", handler);
    return () => globalThis.removeEventListener("storage", handler);
  }, [dispatch, isPortalSettignsLoaded, isAuthenticated, isContentPage]);

  useEffect(() => {
    if (isAuthenticated && isContentPage) {
      const userLogin = sessionStorage.getItem("isLogin") !== "false";
      if (userLogin) {
        handleLogin();
      } else {
        loadUserContext();
        refreshUserContext();
      }
    }
  }, [isAuthenticated, isContentPage]);

  useEffect(() => {
    if (courseInProgress) {
      SessionManager.renew();
      SessionManager.enableAutoRenewal();
    } else {
      SessionManager.disableAutoRenewal();
    }
  }, [courseInProgress]);

  const hideToast = () => {
    setTimeout(function () {
      dispatch({ type: "alert/hide" });
    }, 5000);
  };
  toasterAlert.type == "success" ? hideToast() : "";
  const handleHeaderClick = (type: string) => {
    switch (type) {
      case HEADER_CLICK_TYPES.SURVEY:
        setOpenSurvey(true);
        break;
      case HEADER_CLICK_TYPES.REPORT:
        setShowReportDialog(true);
        break;
      case HEADER_CLICK_TYPES.LOGOUT:
        handleLogout();
        break;
      case HEADER_CLICK_TYPES.SERVICENOW:
        handleServiceNow();
        break;
      case HEADER_CLICK_TYPES.CONTACTUS:
        openContactUs();
        break;
      case BookingJourney_Paths.Summary:
        handleNavigate(BookingJourney_Paths.Summary);
        break;
      default:
        break;
    }
  };

  const { openUrl } = openUrlUtil();

  function handleServiceNow() {
    openUrl(tenantSettings.customConfig.serviceNowUrl, KLP_SERVICE_NOW_WINDOW_NAME);
  }

  function closeSessionTimeoutDialog() {
    setOpenMultiStepSurvey(false);
    setIsTimeout(false);
    handleLogout();
  }

  async function resetTimer() {
    setIsTimeout(false);
    SessionManager.tracker();
    SessionManager.startInterval();
    onSilentRenew();
  }

  function handleLogout() {
    handleClearTokenStore();
  }

  function postLogout() {
    sessionStorage.removeItem("_expiredTime");
    sessionStorage.removeItem("relatedItemClick");
    sessionStorage.removeItem("breadCrumbs");
    localStorage.clear();
    onLogout();
  }

  const [isTimeout, setIsTimeout] = useState(false);
  const alertElement = useRef<HTMLElement>(null);
  useEffect(() => {
    sessionStorage.removeItem("_expiredTime");
    SessionManager.initialize({
      timeout: tenantSettings?.customConfig?.timeout || 1800, //open Idle timer modal after 30 Mints
      onTimeout: () => {
        setIsTimeout(true);
      },
      onExpired: () => {
        //do something if expired on load
        logger.log("onExpired ");
      },
    });
    return () => {
      SessionManager.cleanUp();
    };
  }, []);

  useEffect(() => {
    SessionManager.onAutoRenew = onSilentRenew;
  }, [onSilentRenew]);

  const handleShowSurvey = () => {
    setSuveyConfirmation(false);
    setOpenSurvey(true);
  };

  const handleSurveyCompleted = () => {
    setIsSurveyCompleted(true);
    if (
      !notificationResult?.notificationByLearnerId?.length ||
      notificationResult?.notificationByLearnerId?.length === 0
    ) {
      setOpenNotification(false);
    }
  };

  const showHeaderFooter = () => {
    return location.pathname !== "/logout" && isContentPage;
  };

  useEffect(() => {
    const surveyCount: number = data?.userContext?.showSurvey && !isSurveyCompleted ? 1 : 0;
    const totalNotificationCount: number =
      notificationMetrics?.notificationsMetrics?.totalNotificationsCount || 0;
    setTotalNotificationsCount(surveyCount + totalNotificationCount);
    const unreadNotificationsCount: number =
      notificationMetrics?.notificationsMetrics?.unreadNotificationsCount || 0;
    setTotalUnreadNotificationsCount(surveyCount + unreadNotificationsCount);
  }, [notificationMetrics]);

  return (!isTranslationsLoaded || !isPortalSettignsLoaded) && isContentPage ? (
    <Loader isDisplay={!isTranslationsLoaded || !isPortalSettignsLoaded}></Loader>
  ) : (
    <>
      <Layout
        header={
          showHeaderFooter() ? (
            portalSettingsManager?.application?.features?.enableIBFFlow ? (
              <HeaderV2
                onClick={handleHeaderClick}
                isSurveyCompleted={isSurveyCompleted}
                setOpenNotification={setOpenNotification}
                notificationCount={totalNotificationsCount}
                isClearNotification={isClearNotification}
                newNotificationCount={totalUnreadNotificationsCount}
                onOpenNewLearningStepSurvey={requestOpenNewLearningStepSurvey}
              />
            ) : (
              <Header
                showSearch={portalSettingsManager?.application?.features?.allowContentSearch}
                onClick={handleHeaderClick}
                isSurveyCompleted={isSurveyCompleted}
                setOpenNotification={setOpenNotification}
                notificationCount={totalNotificationsCount}
                isClearNotification={isClearNotification}
                newNotificationCount={totalUnreadNotificationsCount}
              />
            )
          ) : (
            ""
          )
        }
        footer={showHeaderFooter() ? <Footer /> : ""}
        main={routes}
      />
      <Loader type="component" isDisplay={isLoading.show} message={isLoading.message}></Loader>
      {surveyConfirmation && isContentPage && (
        <SurveyConfirmation
          open={surveyConfirmation}
          handleClose={() => setSuveyConfirmation(false)}
          handleShowSurvey={handleShowSurvey}
          handleSkipSurvey={handleSkipSurvey}
          userDisplayName={data?.userContext?.userDisplayName ?? ""}
        />
      )}
      {data?.userContext?.showSurvey && openSurvey && isContentPage && (
        <SurveyDialog
          open={openSurvey}
          handleClose={() => setOpenSurvey(false)}
          onSubmit={() => handleSurveyCompleted()}
          handleSkipSurvey={handleSkipSurvey}
        />
      )}

      {portalSettingsManager?.application?.features?.enableNewLearningRequest &&
        openMultiStepSurvey &&
        isContentPage && (
          <NewLearningSurveyDialog
            open={openMultiStepSurvey}
            handleClose={() => {
              setOpenMultiStepSurvey(false);
            }}
            onSubmitSuccess={() => {
              setOpenMultiStepSurvey(false);
            }}
          />
        )}
      <Drawer
        open={openNotification && isContentPage}
        anchor="right"
        onClose={() => {
          setOpenNotification(false);
        }}
        slotProps={{ paper: { "aria-label": t("notifications") } }}
      >
        <NotificationDrawer
          notificationResult={notificationResult}
          handleRefetchNotifications={handleRefetchNotifications}
          closeHandler={() => setOpenNotification(false)}
          setIsClearNotification={setIsClearNotification}
          showSurvey={data?.userContext?.showSurvey}
          surveyStartDate={data?.userContext?.surveyStartDate}
          isSurveyCompleted={isSurveyCompleted}
          handleHeaderClick={handleHeaderClick}
        />
      </Drawer>
      <SessionTimeoutDialog
        open={isTimeout}
        handleClose={() => closeSessionTimeoutDialog()}
        reset={() => resetTimer()}
        onSessionExpired={() => setOpenMultiStepSurvey(false)}
      />
      <Snackbar
        open={toasterAlert.show}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ boxShadow: "0 14px 20px 0 rgba(0,0,0,0.30)" }}
        autoHideDuration={10000}
        ref={alertElement}
        role="alert"
        aria-labelledby="msg"
        aria-describedby="msg"
        tabIndex={-1}
        classes={{ anchorOriginTopRight: classes.snackBarAnchorOriginTopRight }}
      >
        <Box width={{ xs: "100%", sm: "auto" }}>
          <Alert
            type={toasterAlert.type}
            title={toasterAlert.title}
            message={toasterAlert.message}
            onClose={() => dispatch({ type: "alert/hide" })}
          />
        </Box>
      </Snackbar>
      {portalSettingsManager?.application?.features?.allowChat && <Chatbot />}
      {showReportDialog && (
        <ReportDialog open={true} handleClose={() => setShowReportDialog(false)} />
      )}
    </>
  );
}

export default App;
